import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Gift, Phone } from "lucide-react";

interface NavItem {
  label: string;
  href: string;
  internal?: boolean;
  highlight?: boolean;
  number: string;
}

const ModernNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav: NavItem[] = [
    { number: "01", label: "Warum", href: "#warum" },
    { number: "02", label: "Leistungen", href: "#leistungen" },
    { number: "03", label: "KI-Service", href: "#ki", highlight: true },
    { number: "04", label: "Zeiten", href: "#zeiten" },
    { number: "05", label: "Preise", href: "#preise" },
    { number: "06", label: "FAQ", href: "#faq" },
    { number: "07", label: "Blog", href: "/blog", internal: true },
    { number: "08", label: "Kompakt", href: "/kompakt", internal: true },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Top Bar with Contact Info – Navy aus dem Logo */}
      <div className="bg-navy-deep text-white py-2.5 relative overflow-hidden">
        <div className="absolute inset-0 bg-circuit-light pointer-events-none" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 flex flex-wrap items-center justify-center md:justify-between gap-2 md:gap-4 text-sm md:text-base">
          <a
            href="tel:+4915128958006"
            className="flex items-center gap-2 text-white/90 hover:text-flame-soft transition-colors font-semibold tracking-wide"
          >
            <Phone className="h-4 w-4" />
            <span>01512 895 800 6</span>
          </a>
          <a
            href="https://wa.me/4915128958006?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zum%20IT-Support."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-1.5 rounded-full transition-colors font-semibold"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp</span>
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <div
        className={`border-b transition-all duration-300 ${
          scrolled
            ? "border-border bg-background/85 backdrop-blur-xl shadow-soft"
            : "border-transparent bg-background/70 backdrop-blur-md"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 transition-all duration-300 ${
            scrolled ? "py-2.5 md:py-3" : "py-4 md:py-5"
          }`}
        >
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src="/images/logo-klartech.png"
              alt="Klartech Müritz Logo"
              className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-9 md:h-10" : "h-11 md:h-14"}`}
            />
          </a>

          {/* Desktop Navigation - Numbered */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {nav.map((n) =>
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  className="group relative px-3.5 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-navy"
                >
                  <span className="text-[11px] text-flame font-mono mr-1.5 align-top">{n.number}</span>
                  <span className="relative">
                    {n.label}
                    <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                </Link>
              ) : (
                <button
                  key={n.href}
                  onClick={() => scrollTo(n.href)}
                  className={`group relative px-3.5 py-2 text-sm font-semibold transition-colors ${
                    n.highlight ? "text-primary" : "text-muted-foreground hover:text-navy"
                  }`}
                >
                  <span className="text-[11px] text-flame font-mono mr-1.5 align-top">{n.number}</span>
                  <span className="relative inline-flex items-center gap-1">
                    
                    {n.label}
                    <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                </button>
              )
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            {/* Gift Voucher Button */}
            <Link to="/gutschein">
              <Button
                variant="outline"
                className="hidden md:flex items-center gap-2 rounded-full border-2 border-flame/35 bg-flame/5 px-4 py-2.5 text-flame font-bold hover:bg-flame/10 hover:border-flame/60 transition-all"
              >
                <Gift className="h-4 w-4" />
                Gutschein
              </Button>
            </Link>

            {/* Desktop CTA */}
            <Button
              onClick={() => scrollTo("#kontakt")}
              className="hidden md:flex rounded-full bg-primary hover:bg-primary/90 px-6 py-3 text-primary-foreground shadow-soft transition-all text-sm font-bold tracking-wide hover:-translate-y-0.5"
            >
              Kontakt aufnehmen
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl hover:bg-muted transition-colors"
              aria-label="Menü öffnen"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="h-7 w-7 text-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-7 w-7 text-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - With Numbers */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background shadow-lift">
            <nav className="flex flex-col px-4 py-4 gap-1">
              {nav.map((n) =>
                n.internal ? (
                  <Link
                    key={n.href}
                    to={n.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-lg font-semibold text-muted-foreground py-3 px-4 rounded-xl hover:bg-muted hover:text-navy transition-colors"
                  >
                    <span className="text-sm text-flame font-mono w-6">{n.number}</span>
                    {n.label}
                  </Link>
                ) : (
                  <button
                    key={n.href}
                    onClick={() => scrollTo(n.href)}
                    className={`flex items-center gap-3 text-left text-lg font-semibold py-3 px-4 rounded-xl transition-colors ${
                      n.highlight
                        ? "text-primary bg-primary/5 hover:bg-primary/10"
                        : "text-muted-foreground hover:bg-muted hover:text-navy"
                    }`}
                  >
                    <span className="text-sm text-flame font-mono w-6">{n.number}</span>
                    <span className="inline-flex items-center gap-2">
                      
                      {n.label}
                    </span>
                  </button>
                )
              )}
              <Link
                to="/gutschein"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 text-lg font-bold text-flame py-3 px-4 rounded-xl bg-flame/5 border border-flame/20 hover:bg-flame/10 transition-colors mt-2"
              >
                <Gift className="h-5 w-5" />
                Gutschein verschenken
              </Link>
              <Button
                onClick={() => scrollTo("#kontakt")}
                className="mt-3 w-full rounded-full bg-primary hover:bg-primary/90 px-6 py-6 text-primary-foreground shadow-soft transition-all text-lg font-bold"
              >
                Kontakt aufnehmen
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default ModernNav;
